import { differenceInDays } from 'date-fns';
import type { Bag } from '../types';
import { BagDeadlineBadge } from './BagDeadlineBadge';
import { cn } from '@/lib/utils';

type Props = {
  bag: Bag;
  onClick: (bagId: string) => void;
};

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const getInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

const getAgeLabel = (bag: Bag): string => {
  if (bag.label_generated && bag.closed_at) {
    const days = differenceInDays(new Date(), new Date(bag.closed_at));
    return `etiqueta gerada · há ${days} dias`;
  }
  if (bag.status === 'fechada' && bag.closed_at) {
    const days = differenceInDays(new Date(), new Date(bag.closed_at));
    return `fechada há ${days} dias`;
  }
  const days = differenceInDays(new Date(), new Date(bag.opened_at));
  return `aberta há ${days} dias`;
};

const getContactChannel = (bag: Bag): string => {
  if (bag.customers.instagram) return `@${bag.customers.instagram}`;
  if (bag.customers.whatsapp) return bag.customers.whatsapp;
  return '';
};

export const BagCard = ({ bag, onClick }: Props) => {
  const activeSales = bag.sales.filter((s) => s.payment_status !== 'cancelled');

  const itemsCount = activeSales.reduce(
    (sum, s) => sum + s.sale_items.reduce((qty, i) => qty + i.quantity, 0),
    0
  );

  const total = activeSales.reduce((sum, s) => sum + (s.total ?? 0), 0);
  const initials = getInitials(bag.customers.name);
  const ageLabel = getAgeLabel(bag);
  const channel = getContactChannel(bag);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(bag.id)}
      onKeyDown={(e) => e.key === 'Enter' && onClick(bag.id)}
      className={cn(
        'flex items-center gap-4 rounded-lg border bg-card p-4 shadow-sm',
        'cursor-pointer hover:bg-accent/50 transition-colors'
      )}
    >
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
        {initials}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium truncate">{bag.customers.name}</span>
          {channel && (
            <span className="text-xs text-muted-foreground truncate">{channel}</span>
          )}
        </div>

        <div className="mt-1 flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
          <span>{itemsCount} {itemsCount === 1 ? 'item' : 'itens'}</span>
          <span>{formatCurrency(total)}</span>
          <span>{ageLabel}</span>
          {bag.status === 'aberta' && (
            <BagDeadlineBadge expiresAt={bag.expires_at} />
          )}
        </div>
      </div>
    </div>
  );
};
